import { convertValue } from '@/utils/util-measurements';
import {
  AccountResponse,
  CompressorData,
  CustomerInformationStepData,
  DryerFormType,
  NO_MATCHING_SPEC,
  OnboardingFormPayload,
  PressureTypeFull,
  PressureTypeRoom,
  RoomConfigurationStepData,
  SiteConfigurationStepData,
  TankFormType,
  STATION_USAGE_TYPES,
  STATION_NAME_SUFFIXES,
} from './constants';
import { unitTypes } from '@/constants/measurements';

export const extractUniqueCustomers = (
  stations: Array<{
    customerId?: string;
    customerName?: string;
  }>,
) => {
  return stations.reduce((acc: Array<{ customerName: string; customerId: string }>, station) => {
    const existingCustomer = acc.find(c => c.customerId === station.customerId);
    if (!existingCustomer && station.customerId && station.customerName) {
      acc.push({
        customerName: station.customerName,
        customerId: station.customerId,
      });
    }
    return acc;
  }, []);
};

export const updateCustomerInformation = (
  form: OnboardingFormPayload,
  salesforceData: AccountResponse,
  customerInformationData: CustomerInformationStepData,
) => {
  customerInformationData.customer.accountId = form?.salesforceAccountId;
  customerInformationData.customer.name = form?.siteData?.generalData?.customerName ?? salesforceData.customerName;
  customerInformationData.newCustomerName = form?.siteData?.generalData?.customerName ?? salesforceData.customerName;
  customerInformationData.account = form?.siteData.generalData?.ecoplantCustomerId;

  const backendTier = form?.siteData.generalData?.stationTier;
  const backendUsageType = form?.siteData.generalData?.stationUsageType;
  customerInformationData.tier = backendTier;
  customerInformationData.stationUsageType = backendUsageType || STATION_USAGE_TYPES.REGULAR;

  if (form?.siteData?.generalData?.contactPerson) {
    const { firstName, lastName, email } = form.siteData.generalData.contactPerson;
    customerInformationData.contactPerson.fullName = firstName && lastName ? `${firstName} ${lastName}` : null;
    customerInformationData.contactPerson.email = email || null;
  } else {
    customerInformationData.contactPerson.fullName = null;
    customerInformationData.contactPerson.email = null;
  }

  const { firstName, lastName } = form?.siteData?.generalData?.accountManager ?? {};
  customerInformationData.accountManager.fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  customerInformationData.accountManager.email = form?.siteData?.generalData?.accountManager?.email ?? null;

  customerInformationData.siteAddress.city =
    form?.siteData?.generalData?.siteAddress?.city ?? salesforceData.siteAddress.city;
  customerInformationData.siteAddress.state =
    form?.siteData?.generalData?.siteAddress?.state ?? salesforceData.siteAddress.state;
  customerInformationData.siteAddress.country =
    form?.siteData?.generalData?.siteAddress?.country ?? salesforceData.siteAddress.country;
  customerInformationData.siteAddress.zipCode =
    form?.siteData?.generalData?.siteAddress?.zipCode ?? salesforceData.siteAddress.postalCode;
  customerInformationData.siteAddress.streetAddress =
    form?.siteData?.generalData?.siteAddress?.streetAddress ?? salesforceData.siteAddress.street;
};

export const updateSiteConfiguration = (
  form: OnboardingFormPayload,
  siteConfigurationData: SiteConfigurationStepData,
) => {
  siteConfigurationData.compressorRooms = form?.siteData?.siteGeneralConfiguration?.numberCompressorRooms;
  siteConfigurationData.entryPoints = form?.siteData?.siteGeneralConfiguration?.numberEntryPointsMainHeader;
  const { pressureType } = form?.siteData?.siteGeneralConfiguration || {};
  siteConfigurationData.hasMultipleTargetPressures = pressureType && pressureType !== PressureTypeFull.MAIN;
  siteConfigurationData.systemControllerType = form?.siteData?.siteGeneralConfiguration?.systemControllerType;
  siteConfigurationData.stationNameSuffix =
    form?.siteData?.siteGeneralConfiguration?.stationNameSuffix || STATION_NAME_SUFFIXES.MAIN;
  siteConfigurationData.pressureType = form?.siteData?.siteGeneralConfiguration?.pressureType || PressureTypeFull.MAIN;
};

export const updateRoomConfiguration = (
  form: OnboardingFormPayload,
  roomConfigurationStepData: RoomConfigurationStepData,
  siteConfigurationData: SiteConfigurationStepData,
  tankForms: Record<number, TankFormType>,
  dryerForms: Record<number, DryerFormType>,
): {
  newRoomConfigurationStepData: RoomConfigurationStepData;
  newTankForms: Record<number, TankFormType>;
  newDryerForms: Record<number, DryerFormType>;
} => {
  const { processedRoomsData, newTankForms, newDryerForms } = processRooms(form, tankForms, dryerForms);

  const newRoomConfigurationStepData = {
    ...roomConfigurationStepData,
    rooms: processedRoomsData,
    [siteConfigurationData.pressureType]: {
      rooms: processedRoomsData,
    },
  };

  return { newRoomConfigurationStepData, newTankForms, newDryerForms };
};

const processRooms = (
  form: OnboardingFormPayload,
  tankForms: Record<number, TankFormType>,
  dryerForms: Record<number, DryerFormType>,
): {
  processedRoomsData: PressureTypeRoom[];
  newTankForms: Record<number, TankFormType>;
  newDryerForms: Record<number, DryerFormType>;
} => {
  const newTankForms = { ...tankForms };
  const newDryerForms = { ...dryerForms };
  const processedRoomsData = form?.siteData?.siteGeneralConfiguration?.roomsData?.map((room, index: number) => {
    const { compressorsInRoom, compressorData: originalCompressorData } = room;
    const compressorsData: CompressorData[] = processCompressorData(compressorsInRoom, originalCompressorData);

    const roomTanks = room.tanks?.map(tank => ({
      ...tank,
      tankValue: convertToDisplayUnit(tank.tankValue, unitTypes.LITER),
    }));

    const newTank = roomTanks.slice(roomTanks?.length - 1, roomTanks?.length)?.[0];

    newTankForms[index] = {
      tankType: newTank?.tankType,
      tankValue: newTank?.tankValue,
    };

    const dryers = room.dryers ?? [];
    const newDryer = dryers.slice(dryers?.length - 1, dryers?.length)?.[0];
    newDryerForms[index] = {
      dryerType: newDryer?.dryerType,
    };

    return {
      ...room,
      id: index,
      compressorData: compressorsData,
      tanks: roomTanks.slice(0, roomTanks?.length - 1).map((tank, tankIndex) => ({
        ...tank,
        id: tankIndex,
      })),
      newTank,
      dryers: dryers.slice(0, dryers?.length - 1).map((dryer, dryerIndex) => ({
        ...dryer,
        id: dryerIndex,
      })),
      newDryer: newDryer && Object.keys(newDryer).length > 0 ? newDryer : { dryerType: null },
    };
  });
  return { processedRoomsData, newTankForms, newDryerForms };
};

const convertToDisplayUnit = (value: number, unit: string) => {
  return convertValue(value, unit);
};

const processCompressorData = (
  compressorsInRoom: string[],
  originalCompressorData: CompressorData[],
): CompressorData[] => {
  return originalCompressorData.map((compressor: CompressorData) => {
    const foundCompressor = compressorsInRoom.find((serialNumber: string) => serialNumber === compressor.serialNumber);

    if (compressor.modelSpec === NO_MATCHING_SPEC) {
      return {
        ...compressor,
        additionalInformation: {
          ...(compressor.additionalInformation || {}),
          pressure: convertToDisplayUnit(compressor.additionalInformation?.pressure || 0, unitTypes.BAR),
        },
        isManual: !foundCompressor,
      };
    }

    return {
      ...compressor,
      isManual: !foundCompressor,
    };
  });
};
